import { Box, Button } from "@mui/material";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Cricket from "./cricket/cricket";
import X01 from "./x01/x01";
import { useContext, useEffect, useState } from "react";
import {
  BaseMatchState,
  BOT_DIFFICULTY,
  GAME_TYPE,
  MATCH_TYPE,
} from "../types/match";
import { cloneDeep } from "lodash";
import {
  handleEndLeg,
  initMatchState,
  newMatchHandler,
} from "../util/matchLogic";
import { CricketGameState } from "../types/cricket";
import Homepage from "./homepage";
import { InOut, X01GameState } from "../types/x01";
import styled from "@mui/styled-engine";
import { ScreenSizeContext } from "../contexts/ScreenSizeContext";
import SideMenu from "../components/sideMenu";
import dart_settings from "../assets/images/icons/dart_settings.png";
import {
  getNumPlayersLocalStorage,
  setBotDifficultyLocalStorage,
  setInOutLocalStorage,
  setMatchTypeLocalStorage,
  setX01GameLocalStorage,
} from "../util/localStorage";
import { ROUTE } from "../types/routes";
import { OnlineMatchContext } from "../contexts/OnlineMatchContext";
import colors from "../theme/colors";
import fonts from "../theme/fonts";
import { mediaQueries } from "../theme/breakpoints";
import { defaultRounds } from "../util/halvesLogic";
import Halves from "./halves/Halves";

interface MatchProps {}

export default function Match(props: MatchProps) {
  const { online, roomCode, onlineMatchState } = useContext(OnlineMatchContext);
  const navigate = useNavigate();
  const { state } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [matchState, setMatchState] = useState<BaseMatchState>(initMatchState);
  const [x01Game, setX01Game] = useState(301);
  const [x01InOut, setX01InOut] = useState(InOut.DOUBLE_IN_DOUBLE_OUT);
  const [numPlayers, setNumPlayers] = useState(getNumPlayersLocalStorage());
  const [halvesRounds, setHalvesRounds] = useState(defaultRounds);
  const [restart, setRestart] = useState(false);
  const { height } = useContext(ScreenSizeContext);

  useEffect(() => {
    if (!onlineMatchState || onlineMatchState === matchState) return;
    setMatchState(onlineMatchState);
    // eslint-disable-next-line
  }, [onlineMatchState]);

  useEffect(() => {
    if (state?.restart) {
      setMatchState(newMatchHandler());
    }
  }, [state]);

  const endLeg = (finishedState: CricketGameState | X01GameState) => {
    const newMatchState = handleEndLeg(cloneDeep(matchState), finishedState);
    setMatchState(newMatchState);
  };

  const rematchHandler = () => {
    setRestart(true);
    const rematchState = newMatchHandler();
    rematchState.gameType = matchState.gameType;
    rematchState.matchType = matchState.matchType;
    setMatchState(rematchState);
  };

  const selectGameType = (gameType: GAME_TYPE) => {
    const newMatchState = { ...matchState };
    newMatchState.gameType = gameType;
    setMatchState(newMatchState);
  };

  const selectMatchType = (matchType: MATCH_TYPE) => {
    const newMatchState = { ...matchState };
    newMatchState.matchType = matchType;
    setMatchState(newMatchState);
    setMatchTypeLocalStorage(matchType);
  };

  const selectBotDifficulty = (difficulty: BOT_DIFFICULTY) => {
    const newMatchState = { ...matchState };
    newMatchState.botDifficulty = difficulty;
    setBotDifficultyLocalStorage(difficulty);
    setMatchState(newMatchState);
  };

  const selectX01Game = (x01Game: number) => {
    setX01Game(x01Game);
    setX01GameLocalStorage(x01Game);
  };

  const selectX01InOut = (inOut: InOut) => {
    setX01InOut(inOut);
    setInOutLocalStorage(inOut);
  };

  const startMatch = () => {
    switch (matchState.gameType) {
      case GAME_TYPE.CRICKET:
        navigate(ROUTE.CRICKET_PAGE.url);
        break;
      case GAME_TYPE.X01:
        navigate(ROUTE.X01_PAGE.url);
        break;
      case GAME_TYPE.HALVES:
        setHalvesRounds(halvesRounds);
        navigate(ROUTE.HALVES_PAGE.url);
        break;
    }
  };

  return (
    <Wrapper innerHeight={height}>
      <MenuButtonWrapper>
        <Box visibility={online && roomCode ? "visible" : "hidden"}>
          Room: {roomCode}
        </Box>
        <MenuButton onClick={() => setMenuOpen(true)}>
          <img src={dart_settings} alt="settings" />
        </MenuButton>
      </MenuButtonWrapper>
      <Routes>
        <Route
          path={ROUTE.HOMEPAGE.url}
          element={
            <Homepage
              matchState={matchState}
              resetMatchState={rematchHandler}
              selectGameType={selectGameType}
              selectMatchType={selectMatchType}
              selectBotDifficulty={selectBotDifficulty}
              selectX01Game={selectX01Game}
              selectX01InOut={selectX01InOut}
              startMatch={startMatch}
              rematchHandler={rematchHandler}
              numPlayers={numPlayers}
              setNumPlayers={setNumPlayers}
            />
          }
        />
        <Route
          path={ROUTE.BETA.url}
          element={
            <Homepage
              matchState={matchState}
              resetMatchState={rematchHandler}
              selectGameType={selectGameType}
              selectMatchType={selectMatchType}
              selectBotDifficulty={selectBotDifficulty}
              selectX01Game={selectX01Game}
              selectX01InOut={selectX01InOut}
              startMatch={startMatch}
              rematchHandler={rematchHandler}
              numPlayers={numPlayers}
              setNumPlayers={setNumPlayers}
              beta={true}
            />
          }
        />
        <Route
          path={ROUTE.X01_PAGE.url}
          element={
            <X01
              gameScore={x01Game}
              endLeg={endLeg}
              matchState={matchState}
              inOut={x01InOut}
              rematch={rematchHandler}
              restart={restart}
              setRestart={setRestart}
            />
          }
        />
        <Route
          path={ROUTE.CRICKET_PAGE.url}
          element={
            <Cricket
              endLeg={endLeg}
              matchState={matchState}
              rematch={rematchHandler}
              restart={restart}
              setRestart={setRestart}
            />
          }
        />
        <Route
          path={ROUTE.HALVES_PAGE.url}
          element={
            <Halves
              endLeg={endLeg}
              matchState={matchState}
              rematch={rematchHandler}
              restart={restart}
              setRestart={setRestart}
              numPlayers={numPlayers}
              rounds={halvesRounds}
            />
          }
        />
        <Route
          path={ROUTE.HALFS_PAGE.url}
          element={
            <Halves
              endLeg={endLeg}
              matchState={matchState}
              rematch={rematchHandler}
              restart={restart}
              setRestart={setRestart}
              numPlayers={numPlayers}
              rounds={halvesRounds}
            />
          }
        />
      </Routes>
      <SideMenu
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        rematchHandler={rematchHandler}
        showMultiplayer={matchState.matchType === MATCH_TYPE.BOT}
        handlePlayerChange={() => {
          selectMatchType(
            matchState.matchType === MATCH_TYPE.BOT
              ? MATCH_TYPE.SINGLES
              : MATCH_TYPE.BOT
          );
          setRestart(true);
        }}
        handleChangeGameType={() => {
          if (matchState.gameType === GAME_TYPE.NOT_SELECTED) return;
          selectGameType(
            matchState.gameType === GAME_TYPE.X01
              ? GAME_TYPE.CRICKET
              : GAME_TYPE.X01
          );
        }}
        gameType={matchState.gameType}
      />
    </Wrapper>
  );
}

const Wrapper = styled(Box)<{ innerHeight: number }>(({ innerHeight }) => ({
  height: `${innerHeight * 0.95}px`,
  width: "100vw",
  display: "flex",
  flexDirection: "column",
}));

const MenuButtonWrapper = styled(Box)({
  display: "flex",
  justifyContent: "center",
  color: colors.lightBeige,
  fontSize: fonts.size.mediumLarge,
  [mediaQueries.mobile]: {
    fontSize: fonts.size.medium,
  },
});

const MenuButton = styled(Button)({
  position: "absolute",
  top: 0,
  right: 0,
  img: {
    height: "1.5rem",
  },
});
